import { useEffect, useRef, useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import { useAuth } from '../auth'
import { getShadowUi, saveShadowUi, type ReadingSize, type CitationContent, type CitationStyle } from '../api'
import { isPlayheadTenant } from '../playhead'
import { getGlyph } from '../uiConfig'

type ThemeMode = 'light' | 'dark'
type Density = 'normal' | 'compact'
type Identifiers = 'on' | 'off'

const STORAGE_KEY = 'me_tweaks_v1'

type Tweaks = { theme: ThemeMode; density: Density; identifiers: Identifiers }

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Tweaks>
      return {
        // 'system' removed (Emerson 2026-08-06) — coerce any old/invalid value
        // to 'light' (the app's base theme). Only 'dark' stays dark.
        theme: parsed.theme === 'dark' ? 'dark' : 'light',
        // Density control removed (Emerson 2026-08-06) — pinned to 'normal'.
        density: 'normal',
        // Ken 2026-07-08 tweak #4 — default OFF; new key defaults
        // gracefully on existing localStorage payloads.
        identifiers: parsed.identifiers ?? 'off',
      }
    }
  } catch {
    // ignore — use defaults
  }
  return { theme: 'light', density: 'normal', identifiers: 'off' }
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement
  root.style.colorScheme = mode
  root.setAttribute('data-theme', mode)
}

function applyDensity(density: Density) {
  document.documentElement.style.setProperty(
    '--me-density',
    density === 'compact' ? '0.85' : '1',
  )
  document.documentElement.setAttribute('data-density', density)
}

function applyIdentifiers(identifiers: Identifiers) {
  document.documentElement.setAttribute('data-identifiers', identifiers)
}


// Module-level effect runner: applies persisted tweaks once at app boot
// so theme/density are correct even before TweaksPanel mounts.
let _bootedTweaks = false
function bootTweaksOnce() {
  if (_bootedTweaks) return
  _bootedTweaks = true
  const t = loadTweaks()
  applyTheme(t.theme)
  applyDensity(t.density)
  applyIdentifiers(t.identifiers)
}


/**
 * TweaksPanel renders inline (intended to live in the top bar). It's
 * a compact gear button that pops a card below itself with theme +
 * density toggles. State is persisted to localStorage and applied to
 * the document root.
 */
export default function TweaksPanel() {
  const [open, setOpen] = useState(false)
  const [tweaks, setTweaks] = useState<Tweaks>(() => {
    bootTweaksOnce()
    return loadTweaks()
  })
  const wrapRef = useRef<HTMLDivElement>(null)

  // Reading size — server-backed per-user (not localStorage), so it follows the
  // user across machines. Read from /me; written via the self-only shadow-ui
  // endpoint (read-modify-write so other shadow_ui keys aren't clobbered), then
  // refresh /me so panes 2 & 3 re-scale app-wide. (Maria 2026-10-19)
  const auth = useAuth()
  // KEN-only branding footer (see JSX below). Images come from the tenant config.
  const isKen = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  const wtWordmark = isKen ? getGlyph('footer_wordmark') : null
  const serverReading: ReadingSize =
    (auth.status === 'authenticated' ? auth.user.reading_size : 'normal') || 'normal'
  const [reading, setReading] = useState<ReadingSize>(serverReading)
  const [savingReading, setSavingReading] = useState(false)
  useEffect(() => { setReading(serverReading) }, [serverReading])
  async function changeReading(v: ReadingSize) {
    if (v === reading) return
    const prev = reading
    setReading(v)            // optimistic — the button highlights immediately
    setSavingReading(true)
    try {
      const cur = await getShadowUi()
      await saveShadowUi({ ...cur, reading_size: v })
      auth.refresh()         // re-fetch /me → Workspace re-reads reading_size
    } catch {
      setReading(prev)       // revert on failure
    } finally {
      setSavingReading(false)
    }
  }
  // Citation style (per-user, KEN) — content form + look. Same server-backed
  // read-modify-write as reading size, so it follows the user across machines.
  const serverCiteContent: CitationContent =
    (auth.status === 'authenticated' ? auth.user.citation_content : 'first') || 'first'
  const serverCiteStyle: CitationStyle =
    (auth.status === 'authenticated' ? auth.user.citation_style : 'underline') || 'underline'
  const [citeContent, setCiteContent] = useState<CitationContent>(serverCiteContent)
  const [citeStyle, setCiteStyle] = useState<CitationStyle>(serverCiteStyle)
  const [savingCite, setSavingCite] = useState(false)
  useEffect(() => { setCiteContent(serverCiteContent) }, [serverCiteContent])
  useEffect(() => { setCiteStyle(serverCiteStyle) }, [serverCiteStyle])
  async function saveCite(patch: { citation_content?: CitationContent; citation_style?: CitationStyle }) {
    const prevC = citeContent, prevS = citeStyle
    if (patch.citation_content) setCiteContent(patch.citation_content)
    if (patch.citation_style) setCiteStyle(patch.citation_style)
    setSavingCite(true)
    try {
      const cur = await getShadowUi()
      await saveShadowUi({ ...cur, ...patch })
      auth.refresh()         // re-fetch /me → the chat re-reads the citation setting
    } catch {
      setCiteContent(prevC); setCiteStyle(prevS)
    } finally {
      setSavingCite(false)
    }
  }

  useEffect(() => {
    applyTheme(tweaks.theme)
    applyDensity(tweaks.density)
    applyIdentifiers(tweaks.identifiers)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks))
  }, [tweaks])

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="me-btn me-btn-outline"
        style={{ borderColor: 'var(--me-borderStrong)' }}
        title="Settings"
      >
        <SlidersHorizontal size={isKen ? 15 : 13} />
      </button>
      {open && (
        <div
          className="me-scroll absolute right-0 top-full z-50 mt-1 max-h-[80vh] w-80 overflow-y-auto me-card p-4"
          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[13px] font-semibold me-text">Settings</div>
            <button onClick={() => setOpen(false)} className="me-btn"><X size={12} /></button>
          </div>

          <div className="me-section-h">Theme</div>
          <div className="mb-3 grid grid-cols-2 gap-1">
            {(['light', 'dark'] as ThemeMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setTweaks({ ...tweaks, theme: m })}
                data-selected={tweaks.theme === m ? 'true' : undefined}
                className="me-tab justify-center"
              >
                {m}
              </button>
            ))}
          </div>

          {/* Reading size (Maria 2026-10-19) — bigger type in the chronology +
              reading panes (2 & 3); the matter list stays dense. Per-user, saved
              to the profile so it follows you across machines. */}
          <div className="me-section-h">Reading size</div>
          <div className="mb-1 grid grid-cols-3 gap-1">
            {(['normal', 'large', 'larger'] as ReadingSize[]).map((v) => (
              <button
                key={v}
                onClick={() => changeReading(v)}
                disabled={savingReading}
                data-selected={reading === v ? 'true' : undefined}
                className="me-tab justify-center capitalize"
              >
                {/* UXT-4 A4-4: label the third step "Largest" (stored enum stays 'larger'). */}
                {v === 'larger' ? 'Largest' : v}
              </button>
            ))}
          </div>
          <div className="mb-3 text-[10px] me-text-mute">Bigger text in the reading panes; follows you across machines.</div>

          {/* Citation style (Ken 2026-10-24, KEN only) — two axes, same compact
              footprint as the other sub-tools: CONTENT (which form the in-chat
              citation takes) and STYLE (how it's drawn). */}
          {isKen && (
            <>
              <div className="me-section-h">Citation style</div>
              <div className="mb-1 grid grid-cols-3 gap-1">
                {([['code', 'Code'], ['first', 'Name'], ['first_subject', 'Name + subj']] as [CitationContent, string][]).map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => saveCite({ citation_content: v })}
                    disabled={savingCite}
                    data-selected={citeContent === v ? 'true' : undefined}
                    className="me-tab justify-center"
                  >{label}</button>
                ))}
              </div>
              <div className="mb-3 grid grid-cols-2 gap-1">
                {([['underline', 'Underline'], ['chip', 'Chip']] as [CitationStyle, string][]).map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => saveCite({ citation_style: v })}
                    disabled={savingCite}
                    data-selected={citeStyle === v ? 'true' : undefined}
                    className="me-tab justify-center"
                  >{label}</button>
                ))}
              </div>
            </>
          )}

          {/* Density control removed (Emerson 2026-08-06) — pinned to 'normal'. */}

          {/* Ken 2026-07-08 tweak #4 — IDENTIFIERS toggle. When OFF
              (default), hides M-NNN + matter_type chips under matter
              names in pane-1 & pane-2, and the [composite_id] bracket
              in pane-3's header. Pane-2 chronology "details"
              identifiers are unaffected — those still render when the
              details checkbox is on. */}
          <div className="me-section-h">Identifiers</div>
          <div className="mb-3 grid grid-cols-2 gap-1">
            {(['off', 'on'] as Identifiers[]).map((v) => (
              <button
                key={v}
                onClick={() => setTweaks({ ...tweaks, identifiers: v })}
                data-selected={tweaks.identifiers === v ? 'true' : undefined}
                className="me-tab justify-center"
              >
                {v}
              </button>
            ))}
          </div>

          {/* Branding / copyright footer (Ken 2026-10-22) — KEN only for now
              (playhead-gated); likely promoted to MOVU Main later by removing the
              gate. The WorkingThinking wordmark + the tiny Moravian logo are tenant
              images pulled from ui_config (footer_wordmark / footer_moravian); each
              renders only once its art is loaded, so the text shows on its own until
              then. Aspect-preserving <img> (not the square <Glyph>) since these are
              wide/branding marks, not currentColor icons. */}
          {isKen && (
            // About — a titled SECTION, not a control (Ken 2026-10-23): the heading
            // matches the "Settings" panel title (not the me-section-h small-caps
            // used for controls), and the body is left-aligned sentence case, a step
            // up from the old tiny centred attribution.
            <div className="mt-4 border-t me-border pt-3">
              <div className="mb-2 text-[13px] font-semibold me-text">About</div>
              {wtWordmark && (
                <div className="mb-2">
                  <img
                    src={wtWordmark}
                    alt="WorkingThinking"
                    style={{ height: 18, width: 'auto', maxWidth: 200, objectFit: 'contain' }}
                  />
                </div>
              )}
              <p className="text-[11px] leading-relaxed me-text-mute">Copyright and patents pending. WorkingThinking, 2026.</p>
              <p className="text-[11px] leading-relaxed me-text-mute">Co-created at the Moravian University AI Innovation Hub, Bethlehem, PA.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
